"use client";

import { loadImage } from "@/lib/image-utils";
import { createClientLogger } from "@/lib/logging/client";

const logger = createClientLogger("components/image-upload/image-moderation");

type ImageModerationResult =
  | { ok: true; skipped?: boolean }
  | { ok: false; reason: string };

const MODERATION_IMAGE_MAX_DIMENSION = 768;
const MODERATION_IMAGE_QUALITY = 0.88;
const SERVER_MODERATION_MAX_BYTES = 2 * 1024 * 1024;

async function canvasToJpegFile(
  canvas: HTMLCanvasElement,
  sourceFile: File,
): Promise<File> {
  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, "image/jpeg", MODERATION_IMAGE_QUALITY);
  });

  if (!blob) {
    throw new Error("Could not render image moderation preview.");
  }

  const baseName = sourceFile.name.replace(/\.[^/.]+$/, "") || "image";
  return new File([blob], `${baseName}-moderation.jpg`, {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
}

async function createServerReviewFile(file: File): Promise<File | null> {
  try {
    const img = await loadImage(file);
    const sourceWidth = img.naturalWidth || img.width;
    const sourceHeight = img.naturalHeight || img.height;
    const ratio = Math.min(
      1,
      MODERATION_IMAGE_MAX_DIMENSION / sourceWidth,
      MODERATION_IMAGE_MAX_DIMENSION / sourceHeight,
    );
    const width = Math.max(1, Math.round(sourceWidth * ratio));
    const height = Math.max(1, Math.round(sourceHeight * ratio));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Could not initialize image moderation canvas.");
    }

    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";
    context.drawImage(img, 0, 0, width, height);

    const reviewFile = await canvasToJpegFile(canvas, file);
    if (reviewFile.size <= SERVER_MODERATION_MAX_BYTES) {
      return reviewFile;
    }

    logger.warn("Image moderation preview exceeded server action limit", {
      fileName: file.name,
      previewBytes: reviewFile.size,
    });
    return null;
  } catch (error) {
    logger.warn("Could not create image moderation preview", {
      fileName: file.name,
      error,
    });

    if (file.size <= SERVER_MODERATION_MAX_BYTES) {
      return file;
    }

    return null;
  }
}

export async function moderateImageFile(
  file: File,
): Promise<ImageModerationResult> {
  if (!file.type.startsWith("image/")) {
    return { ok: true, skipped: true };
  }

  try {
    const reviewFile = await createServerReviewFile(file);
    if (!reviewFile) {
      logger.warn(
        "Blocking upload because moderation preview creation failed",
        {
          fileName: file.name,
          fileBytes: file.size,
        },
      );
      return {
        ok: false,
        reason: `Could not review "${file.name}" for moderation. Try a smaller image.`,
      };
    }

    const formData = new FormData();
    formData.append("file", reviewFile, reviewFile.name);
    formData.append("fileName", file.name);

    const response = await fetch("/api/moderation/image", {
      method: "POST",
      body: formData,
      credentials: "same-origin",
    });
    const moderation = (await response.json()) as ImageModerationResult;
    if (!response.ok && moderation.ok) {
      throw new Error(`Image moderation failed with ${response.status}`);
    }
    return moderation;
  } catch (error) {
    logger.warn("Image moderation failed; blocking upload", {
      fileName: file.name,
      error,
    });
    return {
      ok: false,
      reason: `Could not review "${file.name}" for moderation. Try again in a moment.`,
    };
  }
}
